import {
  ApolloCache,
  DefaultContext,
  MutationFunctionOptions,
  MutationHookOptions,
  MutationTuple,
  OperationVariables,
  useMutation as useMutationApollo,
} from '@apollo/client';
import { TypedDocumentNode } from '@graphql-typed-document-node/core';
import { DocumentNode } from 'graphql';
import { get } from 'lodash';
import authenticationService from '@/services/authentication';
import { AppToaster } from '@/utils/toaster';

const VIEW_ONLY_BLOCK_MESSAGE = 'View-only access: mutations are disabled.';
let lastViewOnlyToastAtMs = 0;
const VIEW_ONLY_TOAST_DEDUP_MS = 1500;

type ExtendedOptions = {
  transform?: any;
  selector?: string;
};

export function useMutation<
  TData = any,
  TVariables = OperationVariables,
  TContext = DefaultContext,
  TCache extends ApolloCache<any> = ApolloCache<any>
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: MutationHookOptions<TData, TVariables, TContext> & ExtendedOptions
): MutationTuple<TData, TVariables, TContext, TCache> {
  let { transform, selector, ...options2 } = options || {};

  const trimData = (data: TData | undefined | null) => {
    let result = data;
    if (selector && result) {
      result = get(result, selector);
    }
    if (result && typeof transform === 'function') {
      result = transform(result);
    }
    return result;
  };

  let onCompleted: ((data: TData) => void) | undefined;
  if (options2.onCompleted) {
    onCompleted = (data) => {
      if (options2.onCompleted) {
        options2.onCompleted(trimData(data) as TData);
      }
    };
  }

  let [originalMutation, params] = useMutationApollo<
    TData,
    TVariables,
    TContext,
    TCache
  >(query, {
    ...options2,
    onCompleted,
  });

  const mutationMethod = async (
    args:
      | MutationFunctionOptions<TData, TVariables, TContext, TCache>
      | undefined
  ) => {
    try {
      const result = await originalMutation(args);
      return {
        ...result,
        data: trimData(result?.data) as TData,
      };
    } catch (error: any) {
      const message =
        error?.graphQLErrors?.[0]?.message ||
        error?.networkError?.result?.errors?.[0]?.message ||
        error?.message;

      if (
        authenticationService.isViewOnlyAccessEnabled() &&
        message === VIEW_ONLY_BLOCK_MESSAGE
      ) {
        const now = Date.now();
        if (now - lastViewOnlyToastAtMs > VIEW_ONLY_TOAST_DEDUP_MS) {
          lastViewOnlyToastAtMs = now;
          AppToaster.show({
            intent: 'warning',
            icon: 'eye-open',
            message: 'View-only access: changes are disabled.',
          });
        }
      }

      throw error;
    }
  };

  return [mutationMethod, params];
}
